import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    if (sessionId && !polledRef.current) {
      polledRef.current = true;
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const interval = 2500;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(`${API}/billing/checkout/status/${sid}`);
      setPaymentData(res.data);

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        if (res.data.tenant_id) {
          localStorage.setItem('tenant_id', res.data.tenant_id);
        }
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), interval);
    } catch {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), interval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="billing-success-page" data-testid="billing-success">
      <div className="billing-success-card">
        {status === 'checking' || status === 'processing' ? (
          <>
            <Loader2 className="animate-spin" size={48} style={{ color: '#3182CE' }} />
            <h2>Processing Your Payment</h2>
            <p>Please wait while we confirm your payment and set up your business...</p>
          </>
        ) : status === 'success' ? (
          <>
            <div className="success-icon"><CheckCircle size={48} /></div>
            <h2>Payment Successful!</h2>
            <p>Your business has been created and is ready to use.</p>
            {paymentData?.metadata && (
              <div className="success-details">
                <div className="detail-row"><span>Business:</span><strong>{paymentData.metadata.tenant_name}</strong></div>
                <div className="detail-row"><span>Plan:</span><strong>{paymentData.metadata.plan_slug}</strong></div>
                <div className="detail-row"><span>Amount:</span><strong>${(paymentData.amount_total / 100).toFixed(2)}</strong></div>
              </div>
            )}
            <button className="success-cta" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
              Go to Dashboard <ArrowRight size={18} />
            </button>
          </>
        ) : status === 'timeout' ? (
          <>
            <AlertTriangle size={48} style={{ color: '#D69E2E' }} />
            <h2>Payment Processing</h2>
            <p>Your payment is still being processed. You'll receive a confirmation email shortly.</p>
            <button className="success-cta" onClick={() => navigate('/dashboard')}>Go to Dashboard</button>
          </>
        ) : (
          <>
            <AlertTriangle size={48} style={{ color: '#E53E3E' }} />
            <h2>Payment Issue</h2>
            <p>There was an issue processing your payment. Please try again or contact support.</p>
            <button className="success-cta" onClick={() => navigate('/admin/create-client')}>Try Again</button>
          </>
        )}
      </div>
    </div>
  );
}
